/**
 * <parallax-scene>
 *
 * Firewatch / Alistair-Shepherd-style layered parallax for a single inline SVG.
 * The artwork is authored as transparent depth-layer groups, back-to-front, each
 * tagged with `data-offset` (and optionally `data-offset-x`). One shared, eased
 * scroll value drives every layer; each layer scales it by its own offset, so far
 * layers barely move and near layers move a lot — real depth, and because the
 * layers are transparent they interleave instead of covering each other.
 *
 *   <g data-offset="0">    sky (static)            </g>
 *   <g data-offset="0.15"> far clouds / hills      </g>
 *   <g data-offset="0.4">  mid                     </g>
 *   <g data-offset="0.75"> near foreground + chars </g>   (bigger = nearer)
 *
 * Attributes on <parallax-scene>:
 *   data-travel   base vertical travel in px that offset 1.0 gets across the
 *                 scene's transit through the viewport (default 220)
 *   data-pointer  base horizontal travel in px that offset 1.0 gets from the
 *                 pointer, edge to edge (default 0 = off)
 *   data-ease     lerp factor 0.02–0.5 (default 0.09; lower = floatier)
 *   data-strength global multiplier on all motion (default 1)
 *
 * Notes:
 *   - transforms are written to each <g> via the SVG `transform` attribute in
 *     user units, so there is no CSS transform-box ambiguity
 *   - motion is measured from the scene's position, never from the transform we
 *     applied, so there is no per-frame feedback
 *   - respects `prefers-reduced-motion: reduce`; only runs while on screen
 */
class ParallaxScene extends HTMLElement {
  constructor() {
    super();
    this._layers = [];
    this._fadeNodes = [];
    this._revealed = false;
    this._running = false;
    this._raf = null;
    this._scroll = { current: 0 };          // eased 0..1 progress through viewport
    this._pointer = { tx: 0, cx: 0 };        // eased -1..1 horizontal pointer
    this._onPointer = this._onPointer.bind(this);
    this._tick = this._tick.bind(this);
    this._onReducedChange = this._onReducedChange.bind(this);
  }

  connectedCallback() {
    this._travel = this._toNumber(this.dataset.travel, 220);
    this._pointerTravel = this._toNumber(this.dataset.pointer, 0);
    this._ease = this._clamp(this._toNumber(this.dataset.ease, 0.09), 0.02, 0.5);
    this._strength = this._toNumber(this.dataset.strength, 1);

    this._svg = this.querySelector('svg');
    if (!this._svg) return;

    this._reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._reduced.addEventListener
      ? this._reduced.addEventListener('change', this._onReducedChange)
      : this._reduced.addListener(this._onReducedChange);
    if (this._reduced.matches) return;

    this._collect();

    // Staggered fade-in reveal: groups tagged [data-fade] start hidden and fade
    // in once the scene scrolls into view. Groups tagged data-fade="bottom" are
    // delayed in CSS so lower/nearer characters settle a beat after the top ones,
    // adding a touch of depth. Falls back to fully visible with no JS / reduced
    // motion (the .wp-fade-ready class is what opts a scene into the hide-first
    // state, and it's only ever added here).
    this._fadeNodes = this._svg.querySelectorAll('[data-fade]');
    if (this._fadeNodes.length) this.classList.add('wp-fade-ready');

    if (!this._layers.length && !this._fadeNodes.length) return;

    this._io = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      if (visible) {
        this._reveal();
        this._start();
      } else {
        this._stop();
      }
    }, { threshold: 0, rootMargin: '20% 0px' });
    this._io.observe(this);
  }

  _reveal() {
    if (this._revealed) return;
    this._revealed = true;
    this.classList.add('wp-revealed');
  }

  disconnectedCallback() {
    this._stop();
    if (this._io) this._io.disconnect();
    if (this._reduced) {
      this._reduced.removeEventListener
        ? this._reduced.removeEventListener('change', this._onReducedChange)
        : this._reduced.removeListener(this._onReducedChange);
    }
  }

  _onReducedChange() {
    if (this._reduced.matches) {
      this._stop();
      this._layers.forEach((layer) => layer.el.removeAttribute('transform'));
      // Drop the hide-first state so tagged groups return to fully visible.
      this.classList.remove('wp-fade-ready', 'wp-revealed');
      this._revealed = false;
    } else {
      if (this._fadeNodes.length) {
        this.classList.add('wp-fade-ready');
        if (this._io) this._reveal();
      }
      if (this._layers.length) this._start();
    }
  }

  _collect() {
    const nodes = Array.from(this._svg.querySelectorAll('[data-offset], [data-offset-x]'));
    nodes.forEach((el) => {
      const offset = this._toNumber(el.getAttribute('data-offset'), 0);
      const offsetX = this._toNumber(el.getAttribute('data-offset-x'), 0);
      if (!offset && !offsetX) return;
      el.style.willChange = 'transform';
      this._layers.push({ el, offset, offsetX, y: 0, x: 0 });
    });
  }

  _start() {
    if (this._running || !this._layers.length) return;
    this._running = true;
    if (this._pointerTravel) {
      window.addEventListener('pointermove', this._onPointer, { passive: true });
    }
    this._raf = requestAnimationFrame(this._tick);
  }

  _stop() {
    this._running = false;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
    window.removeEventListener('pointermove', this._onPointer);
  }

  _onPointer(event) {
    const w = window.innerWidth || 1;
    this._pointer.tx = this._clamp((event.clientX / w - 0.5) * 2, -1, 1);
  }

  _tick() {
    if (!this._running) return;

    const vh = window.innerHeight || 1;
    const rect = this.getBoundingClientRect();
    // 0 while the scene is just entering from the bottom, 1 as it leaves the top.
    const raw = (vh - rect.top) / (vh + rect.height || 1);
    const target = this._clamp(raw, 0, 1);

    this._scroll.current += (target - this._scroll.current) * this._ease;
    this._pointer.cx += (this._pointer.tx - this._pointer.cx) * this._ease;

    // one px→user-unit conversion for the whole svg (uniform scale)
    let scale = 1;
    try {
      const m = this._svg.getScreenCTM();
      if (m && m.a) scale = Math.abs(m.a);
    } catch (err) { /* not rendered yet */ }
    const inv = 1 / (scale || 1);

    // centre the drift on mid-transit so layers sit at rest when the scene is
    // roughly centred, then drift symmetrically as it passes.
    const s = (0.5 - this._scroll.current) * 2; // +1 entering .. -1 leaving

    for (let i = 0; i < this._layers.length; i++) {
      const layer = this._layers[i];
      const targetY = s * layer.offset * this._travel * this._strength * 0.5 * inv;
      const targetX = this._pointer.cx * layer.offsetX * this._pointerTravel * this._strength * inv;
      layer.y = targetY;
      layer.x = targetX;
      const y = Math.round(layer.y * 100) / 100;
      const x = Math.round(layer.x * 100) / 100;
      layer.el.setAttribute('transform', 'translate(' + x + ' ' + y + ')');
    }

    this._raf = requestAnimationFrame(this._tick);
  }

  _toNumber(value, fallback) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  }

  _clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
}

if (!customElements.get('parallax-scene')) {
  customElements.define('parallax-scene', ParallaxScene);
}
